import React, { useState, useEffect } from 'react';
import { FaTelegram, FaFacebook, FaInstagram, FaGlobe, FaPhone, FaEnvelope, FaArrowRight, FaCheck, FaRegCopy } from 'react-icons/fa';
import { BASE_URL } from "../../../apiConfig";

const TEAL = '#0ab39c';
const TEAL_BRIGHT = '#14d9bf';

const BRAND_ICON_BG = {
  telegram: 'linear-gradient(135deg, #2AABEE, #229ED9)',
  facebook: 'linear-gradient(135deg, #1877F2, #0C58C9)',
  instagram: 'linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)',
  globe: 'linear-gradient(135deg, #0ab39c, #067a68)',
  email: 'linear-gradient(135deg, #0ab39c, #067a68)',
  phone: 'linear-gradient(135deg, #0ab39c, #067a68)',
};

function Backdrop() {
  return (
    <>
      <div className="fixed inset-0 -z-20" style={{ backgroundColor: '#040f0d' }} />
      <div
        className="fixed -top-24 -right-24 w-[420px] h-[420px] rounded-full -z-10 blur-[10px]"
        style={{ background: `radial-gradient(circle at 30% 30%, ${TEAL_BRIGHT}55, ${TEAL}22 55%, transparent 70%)` }}
      />
      <div
        className="fixed -bottom-32 -left-32 w-[380px] h-[380px] rounded-full -z-10 blur-[10px]"
        style={{ background: `radial-gradient(circle at 60% 60%, ${TEAL_BRIGHT}45, ${TEAL}1e 55%, transparent 70%)` }}
      />
      <svg className="fixed top-0 left-0 w-64 h-64 -z-10 opacity-20" viewBox="0 0 200 200">
        {[...Array(6)].map((_, i) => (
          <path
            key={i}
            d={`M -20 ${20 + i * 22} Q 60 ${i * 15}, 220 ${40 + i * 18}`}
            stroke={TEAL}
            strokeWidth="1"
            fill="none"
          />
        ))}
      </svg>
      <svg className="fixed bottom-0 right-0 w-40 h-40 -z-10 opacity-30" viewBox="0 0 160 160">
        {[...Array(8)].map((_, r) =>
          [...Array(8)].map((_, c) => (
            <circle key={`${r}-${c}`} cx={10 + c * 18} cy={10 + r * 18} r="1.4" fill={TEAL} />
          ))
        )}
      </svg>
    </>
  );
}


function GlassRow({ iconBg, icon, title, subtitle, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 rounded-2xl px-5 py-4 transition-all duration-200"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))',
        border: '1px solid rgba(10,179,156,0.28)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.03) inset, 0 8px 24px -12px rgba(0,0,0,0.5)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(20,217,191,0.6)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(10,179,156,0.28)')}
    >
      <div
        className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-white text-lg shadow-md"
        style={{ background: iconBg }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-[13px] tracking-[0.06em] text-white uppercase">{title}</p>
        <p className="text-[13px] text-white/45 truncate">{subtitle}</p>
      </div>
      <div
        className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center border transition-colors"
        style={{ borderColor: 'rgba(10,179,156,0.5)', color: TEAL_BRIGHT }}
      >
        <FaArrowRight className="text-xs group-hover:translate-x-0.5 transition-transform" />
      </div>
    </a>
  );
}

function CopyLine({ label, value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <button
      onClick={handleCopy}
      className="w-full text-left rounded-2xl px-4 py-3.5 transition-colors"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))',
        border: '1px solid rgba(10,179,156,0.28)',
      }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">{label}</p>
      <div className="mt-1 flex items-center justify-between">
        <p className="text-[14px] font-medium text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          {value}
        </p>
        <span style={{ color: copied ? TEAL_BRIGHT : 'rgba(255,255,255,0.3)' }}>
          {copied ? <FaCheck className="text-xs" /> : <FaRegCopy className="text-xs" />}
        </span>
      </div>
    </button>
  );
}

export default function Page() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/config`);
        if (!response.ok) throw new Error('Failed to fetch configuration');
        const data = await response.json();
        setConfig(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const fontImport = (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');
      .font-body { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
      @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
    `}</style>
  );

  if (loading) {
    return (
      <div className="min-h-screen font-body flex items-center justify-center p-6 relative">
        {fontImport}
        <Backdrop />
        <div className="flex items-center space-x-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TEAL, animation: 'bounce 1.4s infinite ease-in-out' }}></div>
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TEAL, animation: 'bounce 1.4s infinite ease-in-out 0.2s' }}></div>
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TEAL, animation: 'bounce 1.4s infinite ease-in-out 0.4s' }}></div>
        </div>
        <style>{`@keyframes bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen font-body flex items-center justify-center p-6 relative">
        {fontImport}
        <Backdrop />
        <div
          className="max-w-sm w-full rounded-3xl p-8 text-center"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(10,179,156,0.28)' }}
        >
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(239,68,68,0.12)' }}>
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-lg font-semibold text-white mb-1.5">Une erreur est survenue</h2>
          <p className="text-white/45 text-sm mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-7 py-3 text-white rounded-xl font-medium text-sm transition-transform hover:-translate-y-0.5"
            style={{ backgroundColor: TEAL }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!config) return null;

  const hasDirectLines = config.phone_payment || config.phone_stock || config.phone_change;

  return (
    <div className="min-h-screen font-body relative py-8 px-5">
      {fontImport}
      <Backdrop />

      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src={`/images/nextlead-logo-whitee.png`}  className="mx-auto w-64 h-14 object-contain" />
    
          <div className="flex items-center justify-center gap-2 mt-5">
            <span className="w-8 h-[2px] rounded-full" style={{ backgroundColor: TEAL }}></span>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TEAL }}></span>
          </div>
          <p className="mt-3 text-white/55 text-xs leading-relaxed">
            Restez connecté avec NextLead
            <br />
            sur toutes nos plateformes.
          </p>
        </div>

        {/* Social */}
        <div className="flex flex-col gap-4">
          {config.telegram && (
            <GlassRow iconBg={BRAND_ICON_BG.telegram} icon={<FaTelegram />} title="Telegram" subtitle="Rejoignez-nous sur Telegram" href={config.telegram} />
          )}
          {config.facebook && (
            <GlassRow iconBg={BRAND_ICON_BG.facebook} icon={<FaFacebook />} title="Facebook" subtitle="Suivez notre page Facebook" href={config.facebook} />
          )}
          {config.instagram && (
            <GlassRow iconBg={BRAND_ICON_BG.instagram} icon={<FaInstagram />} title="Instagram" subtitle="Découvrez notre univers" href={config.instagram} />
          )}
          <GlassRow iconBg={BRAND_ICON_BG.globe} icon={<FaGlobe />} title="Site web" subtitle="Visitez notre site officiel" href="/" />
        </div>

        {/* Primary contact */}
        {(config.email || config.phone_support) && (
          <div className="flex flex-col gap-4 mt-4">
            {config.email && (
              <GlassRow
                iconBg={BRAND_ICON_BG.email}
                icon={<FaEnvelope />}
                title="Email"
                subtitle={config.email}
                href={`mailto:${config.email}`}
              />
            )}
            {config.phone_support && (
              <GlassRow
                iconBg={BRAND_ICON_BG.phone}
                icon={<FaPhone />}
                title="Support"
                subtitle={config.phone_support}
                href={`tel:${config.phone_support}`}
              />
            )}
          </div>
        )}

        {/* Direct lines */}
        {hasDirectLines && (
          <div className="mt-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/35 mb-3 px-1">
              Lignes directes
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {config.phone_payment && <CopyLine label="Paiement" value={config.phone_payment} />}
              {config.phone_stock && <CopyLine label="Stock" value={config.phone_stock} />}
              {config.phone_change && <CopyLine label="Change" value={config.phone_change} />}
            </div>
          </div>
        )}

        {/* Footer mark */}
        <div className="flex flex-col items-center mt-14">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
            style={{ backgroundColor: '#04110D', border: `1.5px solid ${TEAL}` }}
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7">
              <path d="M2 13 L9 20 L22 4" stroke={TEAL} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
          <p className="font-bold text-sm tracking-[0.2em]" style={{ color: TEAL_BRIGHT }}>NEXTLEAD</p>
          <p className="text-white/35 text-xs mt-1">Votre succès, notre mission.</p>
        </div>

        <p className="text-center text-white/20 text-[11px] mt-8">© 2026 NextLead. Tous droits réservés.</p>
      </div>
    </div>
  );
}